// Sea colour by distance from land (turquoise shallows -> deep blue), in one place so the GLSL the water shader
// runs, the CPU colours of the open-sea ring and the unit tests all agree. Deep water is the open-sea colour,
// so the board's far water joins SeaRing / OpenSea without a seam.

import * as THREE from 'three';
import { SEA_COLOR } from '../sceneRenderConfig.js';

/** [distance from land (world units), sRGB hex]. Smoothstep between consecutive stops. */
export const SEA_STOPS: ReadonlyArray<readonly [number, string]> = [
  [0, '#86d6c4'], // sand-washed shallows
  [0.3, '#4fb9bd'], // turquoise
  [0.8, '#2b8dab'], // clear blue
  [1.6, '#216a93'], // mid depth
  [3, SEA_COLOR], // deep: the open-sea colour
];

/** Light sandy-green that the shallows are tinted with where the seabed shelf shows through. */
export const SHELF_COLOR = '#9fd9bf';
export const FOAM_COLOR = '#f1f7f4';

const smooth = (a: number, b: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

const LINEAR_STOPS = SEA_STOPS.map(([d, hex]) => [d, new THREE.Color(hex)] as const);

/** Sea colour (working colour space, as the vertex-colour attributes store it) at distance `d` from land. */
export function seaColorAt(d: number, out: THREE.Color = new THREE.Color()): THREE.Color {
  const stops = LINEAR_STOPS;
  out.copy(stops[0]![1]);
  for (let i = 1; i < stops.length; i++) {
    out.lerp(stops[i]![1], smooth(stops[i - 1]![0], stops[i]![0], d));
  }
  return out;
}

const f = (v: number) => v.toFixed(5);
const vec3 = (c: THREE.Color) => `vec3(${f(c.r)}, ${f(c.g)}, ${f(c.b)})`;

/** GLSL twin of seaColorAt: `vec3 _seaGrad(float d)` plus the shelf/foam colour constants. */
export function seaGradientGLSL(): string {
  const stops = LINEAR_STOPS;
  const lines = [`  vec3 c = ${vec3(stops[0]![1])};`];
  for (let i = 1; i < stops.length; i++) {
    lines.push(
      `  c = mix(c, ${vec3(stops[i]![1])}, smoothstep(${f(stops[i - 1]![0])}, ${f(stops[i]![0])}, d));`,
    );
  }
  return /* glsl */ `
vec3 _seaGrad(float d) {
${lines.join('\n')}
  return c;
}
const vec3 _SHELF = ${vec3(new THREE.Color(SHELF_COLOR))};
const vec3 _FOAM = ${vec3(new THREE.Color(FOAM_COLOR))};`;
}
